import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Risk, RiskStatusEnum, RiskSeverityEnum, VipApi } from 'api-client';
import { TablePagination } from '../ui/TablePagination';
import { AuthContext } from '../../contexts/AuthContext';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/card';
import { Eye, AlertTriangle, Shield, Clock, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '../ui/button';
import { getApiConfiguration } from '../../api/utils';
import { useTeams } from '../../contexts/TeamsContext';

interface PaginationInfo {
  count: number;
  next: string | null;
  previous: string | null;
}

interface VIPRisksTableProps {
  risks: Risk[];
  currentPage: number;
  onPageChange: (page: number) => void;
  totalRisks: number;
  itemsPerPage: number;
  paginationInfo: PaginationInfo;
}

const getSeverityColor = (severity: RiskSeverityEnum | undefined) => {
  switch (severity) {
    case RiskSeverityEnum.Critical:
      return 'bg-red-500/10 text-red-500 border-red-500/20';
    case RiskSeverityEnum.High:
      return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
    case RiskSeverityEnum.Medium:
      return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
    case RiskSeverityEnum.Low:
      return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
    default:
      return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
  }
};

const getStatusColor = (status: RiskStatusEnum | undefined) => {
  switch (status) {
    case RiskStatusEnum.Active:
      return 'bg-red-500/10 text-red-500 border-red-500/20';
    case RiskStatusEnum.Mitigated:
      return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
    case RiskStatusEnum.Resolved:
      return 'bg-green-500/10 text-green-500 border-green-500/20';
    default:
      return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
  }
};

const getSeverityIcon = (severity: RiskSeverityEnum | undefined) => {
  switch (severity) {
    case RiskSeverityEnum.Critical:
    case RiskSeverityEnum.High:
      return <AlertTriangle className="h-4 w-4" />;
    case RiskSeverityEnum.Medium:
      return <Shield className="h-4 w-4" />;
    case RiskSeverityEnum.Low:
      return <Shield className="h-4 w-4" />;
    default:
      return <Shield className="h-4 w-4" />;
  }
};

export const VIPRisksTable: React.FC<VIPRisksTableProps> = ({
  risks,
  currentPage,
  onPageChange,
  totalRisks,
  itemsPerPage,
  paginationInfo,
}) => {
  // Create a local state for risks so that we can update it client-side
  const [localRisks, setLocalRisks] = useState<Risk[]>(risks);

  // Sync local risks when props change
  useEffect(() => {
    setLocalRisks(risks);
  }, [risks]);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-800/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0F1629] border-b border-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  Title
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  Severity
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  Source
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  Created
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                  Updated
                </th>
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {localRisks.map((risk) => (
                <tr
                  key={risk.id}
                  className="bg-[#0F1629] hover:bg-gray-800/30 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <Link 
                        to={`/vip-risk/${risk.id}`}
                        className="p-2 rounded-full text-gray-400 "
                      >
                        <span className="text-sm font-medium text-white">
                          {risk.title}
                        </span>
                      </Link>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getSeverityColor(risk.severity)}`}>
                        {getSeverityIcon(risk.severity)}
                        <span className="ml-1">{risk.severity}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(risk.status)}`}>
                      <Clock className="h-4 w-4 mr-1" />
                      {risk.status}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-400">
                      {risk.source || 'N/A'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center text-sm text-gray-400">
                      <Calendar className="h-4 w-4 mr-2" />
                      {risk.createdAt && format(new Date(risk.createdAt), 'MMM d, yyyy')}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center text-sm text-gray-400">
                      <Calendar className="h-4 w-4 mr-2" />
                      {risk.updatedAt && format(new Date(risk.updatedAt), 'MMM d, yyyy')}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center">
                      <Link 
                        to={`/vip-risk/${risk.id}`}
                        className="p-2 hover:bg-gray-700/50 rounded-full text-gray-400 hover:text-white transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <TablePagination
        currentPage={currentPage}
        totalItems={totalRisks}
        itemsPerPage={itemsPerPage}
        onPageChange={onPageChange}
        paginationInfo={paginationInfo}
      />
    </div>
  );
};
